import { get, omit } from 'lodash-es';
import {
  Flex,
  FormFeedback,
  FormGroup,
  Switch,
  SwitchProps,
  useConfiguredForm,
} from '../index';

import { useFormContext } from 'react-hook-form';
import clsx from 'clsx';
import React from 'react';
type CommonProps = {
  id?: string;
  name: string;
  variant: 'SINGLE' | 'DOUBLE';
  helperText?: string;
  direction?: 'row' | 'column';
  formGroupClassName?: string;
  onChangeValue?: (val: boolean) => void;
} & Pick<SwitchProps, 'size' | 'disabled'>;
type SingleProps = {
  variant: 'SINGLE';
  label: React.ReactNode;
  labelDirection?: 'left' | 'right';
} & CommonProps;

type DoubleProps = {
  variant: 'DOUBLE';
  rightLabel: React.ReactNode;
  leftLabel: React.ReactNode;
} & CommonProps;

export type SwitchFieldProps = SingleProps | DoubleProps;

type Props<TKeys extends string> = SwitchFieldProps & {
  translate: (key: TKeys) => string;
};

const SwitchField = <TKeys extends string>({
  helperText,
  translate,
  direction = 'row',
  formGroupClassName = 'mb-2',
  onChangeValue,
  ...props
}: Props<TKeys>) => {
  const configurationContext = useConfiguredForm();
  const methods = useFormContext();
  const errorMessage = get(methods.formState.errors, [
    ...props.name.split('.'),
    'message',
  ]) as TKeys;
  const watchedValue = methods.watch(props.name);

  return (
    <FormGroup className={formGroupClassName}>
      <Flex
        alignItems={direction === 'row' ? 'center' : ''}
        className='space-x-2'
        direction={direction}
      >
        {(props.variant === 'DOUBLE' || props.labelDirection !== 'right') && (
          <label
            className={clsx({
              'text-primary': !watchedValue,
              'fw-normal': props.variant === 'DOUBLE' && watchedValue,
              'fw-semi-bold': props.variant === 'DOUBLE' && !watchedValue,
              'mb-0': direction === 'row',
              'mb-3': direction === 'column',
            })}
            onClick={(e) => {
              e.stopPropagation();
            }}
            htmlFor={props.id || props.name}
          >
            {props.variant === 'SINGLE' ? props.label : props.leftLabel}
          </label>
        )}
        <div
          className={configurationContext.mode === 'READ_ONLY' ? 'd-none' : ''}
        >
          <Switch
            {...omit(props, ['leftLabel', 'rightLabel', 'variant'])}
            id={props.id || props.name}
            label={undefined}
            {...methods.register(props.name, {
              onChange(event: React.ChangeEvent<HTMLInputElement>) {
                onChangeValue?.(event.target.checked);
              },
            })}
            isInvalid={!!errorMessage}
            size={props.size || 'sm'}
          />
        </div>
        {configurationContext.mode === 'READ_ONLY' && (
          <Switch
            id={`readOnly.${props.id || props.name}`}
            checked={methods.getValues(props.name)}
            disabled
            size={props.size || 'sm'}
          />
        )}

        {(props.variant === 'DOUBLE' || props.labelDirection === 'right') && (
          <label
            className={clsx('mb-0 ms-1', {
              'text-primary': !!watchedValue,
              'fw-normal': props.variant === 'DOUBLE' && !watchedValue,
              'fw-semi-bold': props.variant === 'DOUBLE' && !!watchedValue,
            })}
            onClick={(e) => {
              e.stopPropagation();
            }}
            htmlFor={props.id || props.name}
          >
            {props.variant === 'DOUBLE' ? props.rightLabel : props.label}
          </label>
        )}
      </Flex>

      {errorMessage ? (
        <FormFeedback invalid>{translate(errorMessage)}</FormFeedback>
      ) : helperText ? (
        <FormFeedback>{helperText}</FormFeedback>
      ) : null}
    </FormGroup>
  );
};

export default SwitchField;
